/**
 * useUserAnalytics Hook
 *
 * Core hook for the global user analytics tracking system.
 * Tracks page visits across the entire site and detects pre-existing users.
 *
 * Features:
 * - Automatic page visit tracking via Next.js router
 * - Dynamic section/subsection categorization from pathname
 * - Pre-existing user detection via localStorage/cookie signals
 * - Pre-existing users get +1 bonus on first section visit (so first visit = 2)
 * - Analytics events pushed to Vercel Analytics and GTM
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { analytics } from 'lib/analytics/analytics';
import { USER_ANALYTICS_EVENTS, USER_ANALYTICS_GTM_EVENTS } from './constants';
import {
  UserAnalyticsData,
  UserAnalyticsContextValue,
  DEFAULT_USER_ANALYTICS,
} from './types';
import {
  getStorageData,
  setStorageData,
  checkForExistingSignals,
  getPageSection,
  getPageSubsection,
  pushToDataLayer,
  createInitialData,
  getDeviceType,
} from './utils';

const runWhenIdle = (cb: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(cb, { timeout: 2000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(cb, 200);
  return () => window.clearTimeout(id);
};

// Routes whose second URL segment is a dynamic value — normalized back to the
// route-template form so analytics dimensions keep their pre-migration shape
// (next/navigation exposes actual values, next/router exposed templates).
const DYNAMIC_SUBSECTION_TEMPLATES: Record<string, string> = {
  order: '[orderNo]',
  'for-work': '[companyCode]',
  advisors: '[advisorId]',
  'cycle-report': '[token]',
  ogdb: '[foodId]',
};
const STATIC_ORDER_CHILDREN = new Set([
  'home',
  'accessories',
  'ring-pro',
  'm1',
  'blood-vision',
  'ring',
]);
const STATIC_OGDB_CHILDREN = new Set(['search']);

const normalizeTrackedPathname = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2) return pathname;
  const [section, second] = segments;
  const template = DYNAMIC_SUBSECTION_TEMPLATES[section];
  if (!template) return pathname;
  if (section === 'order' && STATIC_ORDER_CHILDREN.has(second)) return pathname;
  if (section === 'ogdb' && STATIC_OGDB_CHILDREN.has(second)) return pathname;
  return `/${[section, template, ...segments.slice(2)].join('/')}`;
};

/**
 * Main hook for user analytics tracking.
 * Should be used within UserAnalyticsProvider.
 */
export const useUserAnalytics = (): UserAnalyticsContextValue => {
  const internalPathname = useInternalPathname();
  const [data, setData] = useState<UserAnalyticsData>(DEFAULT_USER_ANALYTICS);

  // Refs to prevent duplicate tracking
  const isInitialized = useRef(false);
  const lastTrackedPath = useRef<string>('');

  // ==========================================================================
  // Initialization
  // ==========================================================================

  useEffect(() => {
    if (isInitialized.current) return;
    if (typeof window === 'undefined') return;

    isInitialized.current = true;

    return runWhenIdle(() => {
      const stored = getStorageData();

      if (!stored || !stored.firstSeen) {
        // First time user - check for pre-existing signals
        const isPreExisting = checkForExistingSignals();
        const initialData = createInitialData(isPreExisting);

        setStorageData(initialData);
        setData(initialData);

        // Track new/returning user detection event
        const eventName = isPreExisting
          ? USER_ANALYTICS_EVENTS.RETURNING_USER_DETECTED
          : USER_ANALYTICS_EVENTS.NEW_USER_DETECTED;
        const gtmEventName = isPreExisting
          ? USER_ANALYTICS_GTM_EVENTS.RETURNING_USER_DETECTED
          : USER_ANALYTICS_GTM_EVENTS.NEW_USER_DETECTED;

        const eventData = {
          preExistingUser: isPreExisting,
          firstSeen: initialData.firstSeen,
          device: getDeviceType(),
        };

        analytics.track(eventName, eventData);
        pushToDataLayer(gtmEventName, eventData);
      } else {
        setData(stored);
      }
    });
  }, []);

  // ==========================================================================
  // Page Visit Tracking
  // ==========================================================================

  useEffect(() => {
    if (!data.firstSeen) return; // Wait for initialization

    const pathname = normalizeTrackedPathname(internalPathname);

    // Prevent duplicate tracking for same page
    if (lastTrackedPath.current === pathname) {
      return;
    }

    lastTrackedPath.current = pathname;

    return runWhenIdle(() => {
      const section = getPageSection(pathname);
      const subsection = getPageSubsection(pathname);
      const now = new Date().toISOString();

      // Calculate visit increments
      // For pre-existing users: if this is their FIRST visit to this section,
      // we add 2 instead of 1 (to account for assumed prior visits)
      const isFirstSectionVisit = !data.sectionVisits[section];
      const isFirstSubsectionVisit =
        subsection && !data.subsectionVisits[subsection];

      const sectionIncrement =
        data.preExistingUser && isFirstSectionVisit ? 2 : 1;
      const subsectionIncrement =
        data.preExistingUser && isFirstSubsectionVisit ? 2 : 1;

      // Update visit counts
      const newData: UserAnalyticsData = {
        ...data,
        totalVisits: data.totalVisits + 1,
        sectionVisits: {
          ...data.sectionVisits,
          [section]: (data.sectionVisits[section] || 0) + sectionIncrement,
        },
        subsectionVisits: subsection
          ? {
              ...data.subsectionVisits,
              [subsection]:
                (data.subsectionVisits[subsection] || 0) + subsectionIncrement,
            }
          : data.subsectionVisits,
        lastVisit: now,
        lastPage: pathname,
      };

      setStorageData(newData);
      setData(newData);

      // Track analytics event
      const analyticsData = {
        pathname,
        section,
        subsection: subsection || 'none',
        totalVisits: newData.totalVisits,
        sectionVisits: newData.sectionVisits[section],
        subsectionVisits: subsection ? newData.subsectionVisits[subsection] : 0,
        preExistingUser: newData.preExistingUser,
        device: getDeviceType(),
      };

      analytics.track(USER_ANALYTICS_EVENTS.PAGE_VISIT, analyticsData);
      pushToDataLayer(USER_ANALYTICS_GTM_EVENTS.PAGE_VISIT, analyticsData);
    });
  }, [internalPathname, data]);

  // ==========================================================================
  // Getter Functions
  // ==========================================================================

  const getSectionVisits = useCallback(
    (section: string): number => data.sectionVisits[section] || 0,
    [data.sectionVisits],
  );

  const getSubsectionVisits = useCallback(
    (subsection: string): number => data.subsectionVisits[subsection] || 0,
    [data.subsectionVisits],
  );

  // ==========================================================================
  // Return Context Value
  // ==========================================================================

  return useMemo(
    () => ({
      data,
      isPreExistingUser: data.preExistingUser,
      firstSeen: data.firstSeen,
      totalVisits: data.totalVisits,
      getSectionVisits,
      getSubsectionVisits,

      // Common shortcuts
      ringVisits: data.sectionVisits['ring'] || 0,
      ringBuyVisits: data.subsectionVisits['ring/buy'] || 0,
      homeVisits: data.sectionVisits['home'] || 0,
      homeBuyVisits: data.subsectionVisits['home/buy'] || 0,
    }),
    [data, getSectionVisits, getSubsectionVisits],
  );
};

export default useUserAnalytics;
